// src/App.js
import React, { useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ConfigProvider, Result } from 'antd';
import ruRU from 'antd/locale/ru_RU';

import AppLayout          from './components/Layout';
import LoginPage          from './pages/Login';
import AuthCallback       from './pages/Login/AuthCallback';
import EquipmentPage      from './pages/Equipment';
import EmployeesPage      from './pages/Employees';
import ConsumablesPage    from './pages/Consumables';
import ServicesPage       from './pages/Services';
import ActsPage           from './pages/Acts';
import ReturnActsPage     from './pages/Acts/ReturnActsPage';
import ITAnalyticsPage    from './pages/ITAnalytics';
import AdminPage          from './pages/Admin';

import { usePermission, storePermissions, getAccessibleModules } from './hooks/usePermission';
import './App.css';

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 1, staleTime: 30000 } },
});

function isTokenExpired(token) {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp * 1000 < Date.now();
  } catch {
    return true;
  }
}

function clearAuth() {
  ['token', 'login', 'full_name', 'is_admin', 'is_super_admin',
   'is_security', 'cfo_id', 'employee_id', 'permissions'].forEach(k =>
    localStorage.removeItem(k)
  );
}

// ── Обёртка роута с проверкой прав ──────────────────────────────────────────
function ProtectedRoute({ module, level = 'view', children }) {
  const { canView, canEdit } = usePermission(module);
  const hasAccess = level === 'edit' ? canEdit : canView;

  if (!hasAccess) {
    const accessible = getAccessibleModules('view');
    const fallback = accessible[0] ? `/${accessible[0].replace('_', '-')}` : '/no-access';
    return <Navigate to={fallback} replace />;
  }
  return children;
}

export default function App() {
  const [token, setToken] = useState(() => {
    const t = localStorage.getItem('token');
    if (!t || isTokenExpired(t)) {
      clearAuth();
      return null;
    }
    return t;
  });
  const [login, setLogin] = useState(() => localStorage.getItem('login'));

  const handleLogin = (newToken, newLogin, extras = {}) => {
    setToken(newToken);
    setLogin(newLogin);

    // Сохраняем permissions если пришли (Google SSO)
    if (extras.permissions) {
      storePermissions(extras.permissions);
    }
    if (extras.employee_id) {
      localStorage.setItem('employee_id', extras.employee_id);
    }
  };

  const handleLogout = () => {
    clearAuth();
    setToken(null);
    setLogin(null);
    queryClient.clear();
  };

  return (
    <QueryClientProvider client={queryClient}>
      <ConfigProvider locale={ruRU} theme={{ token: { colorPrimary: '#6B21A8', borderRadius: 8 } }}>
        <BrowserRouter>
          <Routes>
            <Route path="/auth/callback" element={<AuthCallback onLogin={handleLogin} />} />

            <Route path="/*" element={
              !token ? (
                <LoginPage onLogin={handleLogin} />
              ) : (
                <Routes>
                  <Route path="/" element={<AppLayout login={login} onLogout={handleLogout} />}>
                    <Route index element={<DefaultRedirect />} />

                    <Route path="equipment/*" element={
                      <ProtectedRoute module="equipment">
                        <EquipmentPage />
                      </ProtectedRoute>
                    } />

                    <Route path="employees/*" element={
                      <ProtectedRoute module="employees">
                        <EmployeesPage />
                      </ProtectedRoute>
                    } />

                    <Route path="services/*" element={
                      <ProtectedRoute module="services">
                        <ServicesPage />
                      </ProtectedRoute>
                    } />

                    <Route path="consumables/*" element={
                      <ProtectedRoute module="consumables">
                        <ConsumablesPage />
                      </ProtectedRoute>
                    } />

                    <Route path="acts/*" element={
                      <ProtectedRoute module="acts">
                        <ActsPage />
                      </ProtectedRoute>
                    } />

                    <Route path="return-acts/*" element={
                      <ProtectedRoute module="return_acts">
                        <ReturnActsPage />
                      </ProtectedRoute>
                    } />

                    <Route path="it-analytics/*" element={
                      <ProtectedRoute module="it_analytics">
                        <ITAnalyticsPage />
                      </ProtectedRoute>
                    } />

                    <Route path="no-access" element={
                      <Result
                        status="403"
                        title="Нет доступа"
                        subTitle="Для вашей учетной записи пока не назначены доступные разделы."
                      />
                    } />

                    <Route path="admin/*" element={
                      <ProtectedRoute module="admin">
                        <AdminPage />
                      </ProtectedRoute>
                    } />
                  </Route>
                </Routes>
              )
            } />
          </Routes>
        </BrowserRouter>
      </ConfigProvider>
    </QueryClientProvider>
  );
}

function DefaultRedirect() {
  const accessible = getAccessibleModules('view');
  if (accessible.length === 0) return <Navigate to="/no-access" replace />;
  const first = accessible[0].replace('_', '-');
  return <Navigate to={`/${first}`} replace />;
}